/**
 * Shared modal dialog wrapper (P2-8, review §5.6).
 *
 * One place for the dialog behaviors every overlay in the app was missing:
 *   - focus moves INTO the dialog on open (the card itself receives focus);
 *   - Tab / Shift+Tab are trapped inside the dialog while it is open;
 *   - Escape closes it (calls onClose);
 *   - focus RESTORES to the previously-focused element on close;
 *   - role="dialog" + aria-modal + aria-labelledby for screen readers.
 *
 * Clicking the overlay deliberately does NOT close — most of our dialogs hold
 * half-filled forms (employee, correction, deductions) and a stray click must
 * not destroy them. Escape and the explicit ✕ are the close affordances.
 *
 * Usage: the caller supplies the dialog CONTENT (header/body/footer) and the
 * id of its heading via `labelledBy`; card sizing variants go in `cardClassName`.
 *
 * @module components/Modal
 */

import { useEffect, useRef, type ReactElement, type ReactNode } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), ' +
  'select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
  /** Called on Escape (and by the caller's own ✕/Cancel buttons). */
  onClose: () => void;
  /** id of the heading element inside the dialog (aria-labelledby). */
  labelledBy: string;
  /** Card sizing/layout classes (width, max-height, padding). Default: max-w-lg. */
  cardClassName?: string;
  children: ReactNode;
}

export function Modal({ onClose, labelledBy, cardClassName, children }: ModalProps): ReactElement {
  const cardRef = useRef<HTMLDivElement>(null);
  // Ref'd so the mount-once effect always calls the latest handler.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect((): (() => void) => {
    const card = cardRef.current;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    card?.focus();

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !card) return;
      const nodes = card.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === card || !card.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !card.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused?.focus();
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={`bg-white rounded-xl shadow-xl w-full outline-none ${cardClassName ?? 'max-w-lg'}`}
      >
        {children}
      </div>
    </div>
  );
}
